import { useMemo } from 'react';
import { useGetCourts } from '../court-management/hooks/useGetCourts';
import { useGetCourtsCalendar } from '../court-details/hooks/useGetCourtCalendar';
import { classifyMultiRead, ReadState } from '../_shared/readState';
import { GetCourtCalendarOutputDto } from '../../models/court/dto/get-court-calendar.dto';

// ---------------------------------------------------------------------------
// Court Pulse data hook — computes per-court occupancy for today.
// Occupancy = booked time / available time (from availability windows).
// ---------------------------------------------------------------------------

export interface CourtArc {
	courtId: number;
	courtName: string;
	/** 0–1 occupancy ratio for today (booked / available hours) */
	occupancy: number;
	/** true if any booking is happening right now */
	isLive: boolean;
	/** Occupied milliseconds (for the arc position in expanded view) */
	bookedRanges: Array<{ start: number; end: number }>;
	/** Available time range in ms (denominator) */
	availableMs: number;
}

/**
 * Merge overlapping time ranges and return total ms.
 */
function mergeRanges(ranges: Array<{ start: number; end: number }>): Array<{ start: number; end: number }> {
	if (ranges.length === 0) return [];
	const sorted = [...ranges].sort((a, b) => a.start - b.start);
	const merged: Array<{ start: number; end: number }> = [{ ...sorted[0] }];
	for (let i = 1; i < sorted.length; i++) {
		const last = merged[merged.length - 1];
		if (sorted[i].start <= last.end) {
			last.end = Math.max(last.end, sorted[i].end);
		} else {
			merged.push({ ...sorted[i] });
		}
	}
	return merged;
}

/**
 * Compute total available milliseconds from the calendar's availability windows.
 * Availabilities have ISO startTime/endTime for the specific day.
 */
export function computeAvailableMs(
	calendar: GetCourtCalendarOutputDto,
	windowStart: Date,
	windowEnd: Date,
): number {
	const avails = calendar.availabilities ?? [];
	if (avails.length === 0) {
		// No availability defined — assume full window
		return windowEnd.getTime() - windowStart.getTime();
	}

	// Clip each availability to the window, then MERGE before summing.
	//
	// Availabilities are stored one row per pricing config, not one per opening
	// block (the API's `expandCompactAvailabilities` splits per config whenever
	// `returnAsOneChunk` is false, which is the default both dashboard forms
	// send). So a court priced at two capacities — the whole point of
	// multi-capacity pricing — has two rows covering each physical hour, and a
	// raw sum reports twice the open time, halving every occupancy figure on
	// this page. `mergeRanges` is already used for the booked ranges below for
	// exactly this reason; the denominator needs it just as much.
	const clipped: Array<{ start: number; end: number }> = [];
	for (const a of avails) {
		const s = Math.max(new Date(a.startTime).getTime(), windowStart.getTime());
		const e = Math.min(new Date(a.endTime).getTime(), windowEnd.getTime());
		if (e > s) clipped.push({ start: s, end: e });
	}

	const totalMs = mergeRanges(clipped).reduce((acc, r) => acc + (r.end - r.start), 0);

	return totalMs > 0 ? totalMs : windowEnd.getTime() - windowStart.getTime();
}

/**
 * Compute occupancy for a single court during a time window.
 * Denominator = sum of availability windows, not 24h.
 *
 * It used to end with a `console.log` of every figure it had just computed,
 * labelled "Debug" and shipped to production. Removed at run 208, and the
 * reason is not tidiness — it was spending the one diagnostic channel the
 * freeze detector depends on. `CourtPulseWidget` is mounted in the navbar, so
 * this runs once per court on every admin route, and a court-calendar response
 * re-renders all of them: `DASHBOARD-46` and `-47` each arrived with their
 * breadcrumb buffer AT Sentry's 100-entry cap, the majority of it this line's
 * output, which is what `longTaskReporter`'s docblock means when it promises
 * that "the default fetch breadcrumbs then say which requests preceded it".
 * Each call also did two `toLocaleTimeString()` conversions per merged range.
 *
 * Exported so the occupancy arithmetic — the numbers an operator reads off the
 * widget — is pinned directly, and so the absence of console output is too.
 */
export function computeOccupancy(
	calendar: GetCourtCalendarOutputDto,
	windowStart: Date,
	windowEnd: Date,
): { occupancy: number; isLive: boolean; bookedRanges: Array<{ start: number; end: number }>; availableMs: number } {
	const availableMs = computeAvailableMs(calendar, windowStart, windowEnd);
	if (availableMs <= 0) return { occupancy: 0, isLive: false, bookedRanges: [], availableMs: 0 };

	const now = Date.now();
	let isLive = false;

	// Collect all booked time ranges
	const ranges: Array<{ start: number; end: number }> = [];

	for (const game of calendar.games ?? []) {
		const s = new Date(game.startTime).getTime();
		// duration is in milliseconds
		const e = s + (game.duration ?? 3600000);
		ranges.push({ start: s, end: e });
		if (s <= now && e >= now) isLive = true;
	}

	for (const session of calendar.coachingSessions ?? []) {
		const s = new Date(session.startTime).getTime();
		const e = new Date(session.endTime).getTime();
		ranges.push({ start: s, end: e });
		if (s <= now && e >= now) isLive = true;
	}

	for (const event of calendar.events ?? []) {
		const s = new Date(event.startTime).getTime();
		// duration is in milliseconds
		const e = s + (event.duration ?? 3600000);
		ranges.push({ start: s, end: e });
		if (s <= now && e >= now) isLive = true;
	}

	for (const blockade of calendar.blockades ?? []) {
		const s = new Date(blockade.startTime).getTime();
		const e = new Date(blockade.endTime).getTime();
		ranges.push({ start: s, end: e });
		// Blockades that overlap now also count as live
		if (s <= now && e >= now) isLive = true;
	}

	// Clip to window and merge overlaps
	const clipped = ranges
		.map(r => ({
			start: Math.max(r.start, windowStart.getTime()),
			end: Math.min(r.end, windowEnd.getTime()),
		}))
		.filter(r => r.end > r.start);

	const merged = mergeRanges(clipped);
	const occupiedMs = merged.reduce((sum, r) => sum + (r.end - r.start), 0);

	return {
		occupancy: Math.min(occupiedMs / availableMs, 1),
		isLive,
		bookedRanges: merged,
		availableMs,
	};
}

/**
 * Hook that returns real-time court occupancy data for the Court Pulse widget.
 * Occupancy = booked time / available hours (per court's availability schedule).
 */
export function useCourtPulseData(locationId: string | undefined) {
	// `isError` PAIRED with the data binding, never bare — and the pairing is the
	// interesting part, because this fold's first draft got it wrong in the
	// direction this campaign has a gate for. That draft branched on
	// `isCourtsError` alone, reasoning that a court list we could not confirm
	// makes `totalOccupancy` an average over the wrong denominator.
	// `background-refetch-blanking-gate` caught it, and the gate is right: a
	// failed BACKGROUND refetch that left a cached list must not blank a working
	// widget (R96-3). `useGetCourts` pins `staleTime`/`cacheTime` to `Infinity`,
	// so such a refetch only follows an explicit invalidation and the stale list
	// is missing at most a just-created court — a far smaller error than hiding
	// the pulse over a transient failure of a read whose data is already on hand.
	//
	// The pairing must be `!courts` on the UNDEFAULTED value rather than
	// `courts.length === 0`: an ANSWERED venue with no courts has `data: []`, and
	// a length test would blank it on a refetch failure, while `isUnreadable`
	// (`isError && data == null`) correctly leaves it answered.
	const {
		data: courts,
		isError: isCourtsError,
		state: courtsReadState,
	} = useGetCourts(locationId ?? '');
	const courtList = courts ?? [];

	// Full day window (today 00:00 → 23:59)
	const now = new Date();
	const windowStart = new Date(now);
	windowStart.setHours(0, 0, 0, 0);
	const windowEnd = new Date(windowStart);
	windowEnd.setHours(23, 59, 59, 999);

	// Fetch calendars for all courts (today)
	const courtIds = courtList.map(c => c.id);
	const calendarResults = useGetCourtsCalendar(
		locationId ? courtIds : [],
		windowStart,
		windowEnd
	);

	// Compute arcs
	const arcs: CourtArc[] = useMemo(() => {
		return courtList.map((court, idx) => {
			const calendarData = calendarResults[idx]?.data;
			if (!calendarData) {
				return { courtId: court.id, courtName: court.name, occupancy: 0, isLive: false, bookedRanges: [], availableMs: 0 };
			}
			const { occupancy, isLive, bookedRanges, availableMs } = computeOccupancy(calendarData, windowStart, windowEnd);
			return { courtId: court.id, courtName: court.name, occupancy, isLive, bookedRanges, availableMs };
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courtList, calendarResults.map(r => r.dataUpdatedAt).join(',')]);

	// What this read IS, as one value. Before run 137 this hook exported
	// `isLoading` and nothing else: there was no `isError` term anywhere in it,
	// and its only consumer destructured `isLoading` without ever reading it. So
	// a refused calendar read, and every moment of a normal page load before the
	// calendars resolved, reached `computeOccupancy`'s `if (!calendarData)` arm
	// and rendered as a confident `occupancy: 0` for every court — "nothing is
	// booked today" about a venue that may be full. Two orderings matter here:
	//
	//   - the COURTS read gates the calendar fan-out (`courtIds` feeds it), so
	//     until courts answers there is nothing to say about occupancy and its
	//     state is the honest one to report;
	//   - an ANSWERED courts read that returned no rows leaves `courtIds` empty
	//     and therefore the fan-out empty, which `classifyMultiRead` calls
	//     `never-issued` and which is genuinely `answered` — the venue has no
	//     courts. That case is spelled out rather than left to the fold.
	const state: ReadState =
		isCourtsError && !courts
			? 'unreadable'
			: courtsReadState !== 'answered'
				? courtsReadState
				: courtIds.length === 0
					? 'answered'
					: classifyMultiRead(calendarResults);

	const isLoading = state === 'loading';
	const totalOccupancy = arcs.length > 0
		? arcs.reduce((sum, a) => sum + a.occupancy, 0) / arcs.length
		: 0;

	return { arcs, isLoading, totalOccupancy, windowStart, windowEnd, state };
}
